import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types';
import Dialog from 'components/Dialog';
import Tr from 'translations/Translations'
import DemandOffersOfferService from "services/DemandOffersOfferService";
import Loading from "components/Loading";
import GridBlock from "components/GridBlock";
import {makeStyles} from "@material-ui/styles";
import {RoundType} from "constants/RoundType";
import DemandAdditionalOffersOfferService from "services/DemandAdditionalOffersOfferService";
import Form from "components/Form";
import PartsTable from "components/PartsTable";
import BoldTypography from "components/BoldTypography";
import useId from "hooks/useId";
import * as Yup from "yup";
import VS from "constants/ValidationShema";
import DoubleButton from "components/popup/components/DoubleButton";
import {connect} from "react-redux";
import Typography from "components/Typography";
import DateFormats from "constants/DateFormats";
import moment from "libs/moment";
import useDebouncedEffect from "hooks/useDebouncedEffect";
import TextField from "@material-ui/core/TextField/TextField";

const useStyle = makeStyles(theme => ({
    verificationDate: {
        marginLeft: theme.spacing(2),
        fontStyle: "italic"
    },
    search: {
        width: '90%'
    },
}))

export const getVerificationConfig = (demandId, stageId, supplierId, roundType, positionId) => ({demandId, stageId, supplierId, roundType, positionId})

function VerificationsPopup(
    {
        onClose,
        config,
        formSubmit
    }) {
    const classes = useStyle();
    const [verificationsList, setVerificationsList] = useState(null);
    const [verificationsListFiltered, setVerificationsListFiltered] = useState(null);
    const formId = useId();
    const [working, setWorking] = useState(false);
    const [searchText, setSearchText] = useState("");

    const fillFilteredList = (verificationsList) => {
        if (!searchText || searchText === "") {
            if (verificationsList !== verificationsListFiltered) {
                setVerificationsListFiltered(verificationsList);
            }
        } else {
            const reg = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), "i");
            setVerificationsListFiltered(verificationsList.filter(pos => pos.search.search(reg) > -1));
        }
    }

    const getVerifications = () => {
        let promise;
        switch (config.roundType) {
            case RoundType.BASE:
                promise = DemandOffersOfferService.getVerifications(config.demandId, config.stageId, config.supplierId);
                break;
            case RoundType.ADDITIONAL_OFFERS:
                promise = DemandAdditionalOffersOfferService.getVerifications(config.demandId, config.stageId, config.positionId, config.supplierId);
                break;
            default:
                throw new Error("Unknown roundType: " + config.roundType);
        }
        promise.then((resp) => {
            if (resp) {
                const respMapped = resp.map((x, i) => ({
                    ...x,
                    identity: "_" + i,
                    name: <>{x.positionName}
                        {x.verificationDate && <Typography component="span" variant="body2" className={classes.verificationDate}>
                            {Tr("verification_date")}: {moment(x.verificationDate).format(DateFormats.DATETIME_FORMAT)}
                        </Typography>}
                    </>,
                    search: x.positionName,
                }))
                setVerificationsList(respMapped);
                fillFilteredList(respMapped);
            } else
                onClose();
        }).finally(() => setWorking(false))
    }
    useEffect(() => {
        getVerifications();
        // eslint-disable-next-line
    }, [])

    useDebouncedEffect(() => {
        fillFilteredList(verificationsList);
    }, {timeout: 800, ignoreInitialCall: true}, [searchText])

    const downloadVerifications = (positionIds) => {
        setWorking(true);

        let promise;
        switch (config.roundType) {
            case RoundType.BASE:
                promise = DemandOffersOfferService.downloadVerifications(config.demandId, config.stageId, config.supplierId, positionIds)
                break;
            case RoundType.ADDITIONAL_OFFERS:
                promise = DemandAdditionalOffersOfferService.downloadVerifications(config.demandId, config.stageId, config.positionId, config.supplierId, positionIds);
                break;
            default:
                throw new Error("Unknown roundType: " + config.roundType);
        }
        promise.finally(() => {
            getVerifications();
        })
    }

    const onSubmit = (values) => {
        let posIds = verificationsList.filter(x => values.list[x.identity]).map(x => x.positionId)
        downloadVerifications(posIds)
    }

    return (
        <Dialog
            open
            title={<TextField className={classes.search} value={searchText} onChange={(e) => setSearchText(e.target.value)} label={Tr('search2')}/>}
            maxWidth='lg'
            onClose={onClose}
            dialogActions={
                <DoubleButton
                    justify="flex-end"
                    labelFirst={Tr('close')}
                    onClickFirst={onClose}
                    labelSecond={Tr("verify_documents")}
                    onClickSecond={() => formSubmit[formId]()}
                    disabledSecond={working}
                    workingSecond={working}
                />
            }
        >
            {
                !verificationsListFiltered ? <Loading/> : <GridBlock
                    column
                >
                    <Form
                        initialValues={{
                            list: verificationsList.reduce((prev, cur) => {
                                prev[cur.positionId] = false;
                                return prev;
                            }, {})
                        }}
                        validationSchema={Yup.object().shape({
                            list: VS.any().test('partSelected', Tr('choose_at_least_one'), (e) => {
                                const selIndex = Object.keys(e).findIndex(x => e[x] === true);
                                return selIndex > -1;
                            })
                        })}
                        onSubmit={onSubmit}
                        id={formId}
                    >
                        {(formikProps) => (
                            <GridBlock
                                column
                            >
                                <PartsTable
                                    label={Tr("documents_verification")}
                                    required={false}
                                    id='list'
                                    formikProps={formikProps}
                                    elementList={verificationsListFiltered}
                                    disabled={working}
                                />
                                {
                                    !(verificationsListFiltered.length) &&
                                    <BoldTypography variant='body1'>
                                        {
                                            Tr('no_options')
                                        }
                                    </BoldTypography>
                                }
                            </GridBlock>
                        )}
                    </Form>

                </GridBlock>
            }

        </Dialog>
    )
}

VerificationsPopup.propTypes = {
    onClose: PropTypes.func.isRequired,
    config: PropTypes.shape({
        demandId: PropTypes.string.isRequired,
        stageId: PropTypes.string.isRequired,
        supplierId: PropTypes.number,
        roundType: PropTypes.oneOf(Object.keys(RoundType)).isRequired,
        positionId: PropTypes.number,
    })
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(VerificationsPopup);
